<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      teamId: '',
      pendingFiles: [],
      isUploading: false,
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  computed: {
    teams() {
      return this.$store.getters['teams/getTeams'];
    },
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.pendingFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    async onFileInputChange(event) {
      const files = Array.from(event.target.files || []);
      this.isUploading = true;
      await Promise.all(
        files.map(async file => {
          try {
            const { blobId, fileUrl } = await uploadFile(file);
            this.pendingFiles.push({
              blobId,
              fileUrl,
              filename: file.name,
              fileType: file.type,
            });
          } catch (error) {
            useAlert(
              this.$t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR', {
                fileName: file.name,
              })
            );
          }
        })
      );
      this.isUploading = false;
      event.target.value = '';
    },
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1);
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          team_id: this.teamId || null,
          attached_file_blob_ids: this.pendingFiles.map(file => file.blobId),
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.TEAM.LABEL') }}
            <select v-model="teamId">
              <option value="">
                {{ $t('CANNED_MGMT.ADD.FORM.TEAM.GLOBAL') }}
              </option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </label>
        </div>
        <div class="w-full">
          <label>{{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}</label>
          <div
            v-if="pendingFiles.length"
            class="flex flex-wrap items-center gap-2 mt-1 mb-2"
          >
            <div
              v-for="(file, index) in pendingFiles"
              :key="file.blobId"
              class="flex items-center gap-1 py-1 px-2 rounded-md bg-n-alpha-black2"
            >
              <img
                v-if="file.fileType.startsWith('image')"
                :src="file.fileUrl"
                class="object-cover w-10 h-10 rounded"
              />
              <span class="text-xs truncate max-w-[10rem]">
                {{ file.filename }}
              </span>
              <NextButton
                xs
                slate
                faded
                icon="i-lucide-x"
                @click.prevent="removePendingFile(index)"
              />
            </div>
          </div>
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            :disabled="isUploading"
            @change="onFileInputChange"
          />
          <span v-if="isUploading" class="text-xs text-n-slate-11">
            {{ $t('CANNED_MGMT.ATTACHMENTS.UPLOADING') }}
          </span>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading ||
              isUploading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
